import React, { useState, useEffect, useRef } from 'react';
import { Stack, Paper, Grid, TextField, Typography, FormControl, InputLabel, MenuItem, Select, Autocomplete } from '@mui/material';

//components imports
import DropdownOption from '../../../dataObjects/DropdownOption';
import { GenericFormObject } from '../dataObjects/GenericFormObject';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import ShortFilerObject from "../dataObjects/ShortFilerObject";

interface FormDataProps {
    formData: GenericFormObject;
    formType: string;
    accountTypeCode: string;
    filerData?: ShortFilerObject | null;
    updateCpfId: (cpfId: string, name:string) => void;
}

const OrganizationFormCollections = ['101', '101 PC', '101 BQ', '101 IEPAC', '101 SFC', '101 WTC'];


const FormGeneralInfo = (props: FormDataProps): JSX.Element => {

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();

    const [form, setForm] = useState<GenericFormObject>(props.formData);
    const [filer, setFiler] = useState<ShortFilerObject | null>(props.filerData || null);
    const [formTypeCode, setFormTypeCode] = useState<string>(props.formType);
    const [isNewFiler, setIsNewFiler] = useState<boolean>(false);

    //state for filer search: autocomplete
    const [searchFilerOpen, setSearchFilerOpen] = useState(false);
    const [filerOptions, setFilerOptions] = useState<readonly DropdownOption[]>([]);
    const [selectedFilerCpfId, setSelectedFilerCpfId] = useState<string>(filer?.cpfId ? filer.cpfId.toString() : "Not Found")
    const [selectedFilerName, setSelectedFilerName] = useState<string>(filer?.fullNameReverse ? filer.fullNameReverse : "")

    const isOrganizationForm = (formType: string): boolean => {
        return OrganizationFormCollections.includes(formType);
    }

    useEffect(() => {
        if (props.accountTypeCode && !form.cpfID && isOrganizationForm(props.formType) && props.accountTypeCode!== 'L')
            getNextCpfIdAvailable(props.accountTypeCode);
    }, [props.accountTypeCode]);

   
    const getNextCpfIdAvailable = (accountTypeCode: string) => {

        fetch(`/Filer/GetNextCpfIdAvailable?accountTypeCode=${accountTypeCode}`)
            .then(res => res.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: AppointTreasurer',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setSelectedFilerCpfId(result.result);
                    setIsNewFiler(true);
                }
            });
    }

    //auto complete
    const filerInputRef = useRef<HTMLInputElement>(null);

    const handleSearchFilerOpen = () => {
        setSearchFilerOpen(true);
    };

    const handleSearchFilerClose = (event: any) => {
        setSearchFilerOpen(false);
        setFilerOptions([]);
    };

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const searchFilerByName = (event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        };
        timeoutRef.current = setTimeout(() => {
            refreshFilerList(searchInput);
        }, 800); // 800ms debounce
    };

    const updateSelectedFiler = (filer: DropdownOption) => {
        let filerCpfId = filer?.value.toString() || "";
        let filerName = filer?.label || "";
        setSelectedFilerCpfId(filerCpfId);
        setSelectedFilerName(filerName);
        props.updateCpfId(filerCpfId,filerName);
    }

    const clearSelectedFiler = () => {
        if (filerInputRef.current) {
            filerInputRef.current.value = "";
        }
    }

    const refreshFilerList = async (searchInput: string) => {
        try {
            if (searchInput) {
                let url = `/Reference/GetFilerListings?includeTestAccounts=true&searchPhrase=${searchInput}`;
                await fetch(url)
                    .then(result => result.json())
                    .then(result => {
                        setFilerOptions(result);
                    });
            }
            else {
                setFilerOptions([]);
            }

        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }


    const getCommitteeName = () => {

        if (formTypeCode === '101 WTC')
            return `${form.nameOfCityOrTown} ${form.ward} ${form.party}`;

        if (form.committeeName)
            return form.committeeName;
        if (form.committee)
            return form.committee.name;

        return "";
    }

    return (
            <Paper sx={{ p: 3 }} >
                <Grid item xs={12}>
                    <Stack marginBottom={2}>
                        <Typography variant="subtitle1" fontWeight={600}> Form General Info</Typography>
                    </Stack>
                    <Stack spacing={2} marginBottom={2}>
                        <FormControl sx={{ m: 1, width: 200 }} size="small">
                            <InputLabel>Form Type</InputLabel>
                            <Select
                                label="Form Type"
                                defaultValue={formTypeCode}
                            >
                                <MenuItem value={'T101'}>T101</MenuItem>
                                <MenuItem value={'P101'}>P101</MenuItem>
                                <MenuItem value={'D103'}>D103</MenuItem>
                                <MenuItem value={'101'}>CPF 101</MenuItem>
                                <MenuItem value={'101 PC'}>CPF 101 PC</MenuItem>
                                <MenuItem value={'101 BQ'}>CPF 101 BQ</MenuItem>
                                <MenuItem value={'101 IEPAC'}>CPF 101 IEPAC</MenuItem>
                                <MenuItem value={'101 WTC'}>CPF 101 WTC</MenuItem>
                                <MenuItem value={'101 SFA'}>CPF 101 SFA</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            margin="normal"
                            label="Committee Name (from the form, for reference)"
                            size="small"
                            defaultValue={getCommitteeName()}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                       
                </Stack>
                    <Stack direction="row" spacing={2} marginBottom={2}>
                        <TextField
                            margin="normal"
                            label="CPF ID"
                            size="small"
                            error={selectedFilerCpfId==='Not Found'}
                            value={selectedFilerCpfId}
                            helperText={isNewFiler ? "This is the New Filer" : ""}
                            InputProps={{
                                readOnly: true,
                            }}
                    />
                    {
                        selectedFilerName && 
                        <TextField
                            margin="normal"
                            label="Filer Name"
                            size="small"
                            fullWidth
                            value={selectedFilerName}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    }
                       
                    </Stack>
                    {
                        isOrganizationForm(formTypeCode) &&
                        <Stack marginBottom={1}>
                            <Typography variant="body2" component="i" color="blue">Please search filer to prevent duplicates, or reopen the closed account:</Typography>
                        </Stack>
                    }
                    {/*auto complete*/}
                    <FormControl size="small">
                    <Autocomplete
                        filterOptions={(x) => x}
                        size="small"
                        sx={{
                            width: 250,
                        }}
                        onChange={(event: any, newValue: DropdownOption | null) => {
                            if (newValue) {
                                updateSelectedFiler(newValue);
                            }
                            else {
                                clearSelectedFiler();
                            }
                        }}
                        open={searchFilerOpen}
                        onOpen={handleSearchFilerOpen}
                        onClose={handleSearchFilerClose}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        getOptionLabel={(option) => `${option.value} ${option.label}`}
                        renderOption={(props, option) =>
                        (
                            <Stack
                                key={option.value}
                                direction="row"
                                display={"flex"}
                                justifyContent={"flex-start"}>
                                <Stack>
                                    <Typography variant='body2' {...props}>
                                        {option.value} {option.label}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                        options={filerOptions}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant='outlined'
                                sx={{width:'500px'}}
                                title='Search for a Filer'
                                inputRef={filerInputRef}
                                label={isOrganizationForm(formTypeCode) ? "Search filer..." : "Search filer here if there is NO CPF ID found"}
                                placeholder={"Type name or ID..."}
                                onChange={searchFilerByName}
                            />
                        )}
                    />
                    </FormControl>
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
            </Paper>
    )
}

export default FormGeneralInfo;